import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Avatar,
  Alert,
  Snackbar,
  LinearProgress,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  FileUpload as FileUploadIcon,
  CheckCircle as CheckCircleIcon,
  Image as ImageIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { DashboardLayout } from "../app/layout/DashboardLayout";
import { productsAPI } from "../services/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

interface ProductFilters {
  search: string;
  category: string;
  brand: string;
  status: string;
  stock_status?: "low" | "out" | "normal";
}

const ProductManagement: React.FC = () => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<ProductFilters>({
    search: "",
    category: "",
    brand: "",
    status: "",
  });
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showBulkUploadDialog, setShowBulkUploadDialog] = useState(false);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  // Fetch products with React Query
  const {
    data: productsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["products", filters, page, rowsPerPage],
    queryFn: () =>
      productsAPI.getProducts({
        ...filters,
        page: page + 1,
        limit: rowsPerPage,
        sort_by: "created_at",
        sort_order: "desc",
      }),
    placeholderData: (previousData) => previousData,
  });

  // Create product mutation
  const createProductMutation = useMutation({
    mutationFn: productsAPI.createProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      setShowAddDialog(false);
      setAlertMessage("Ürün başarıyla oluşturuldu!");
      setShowSuccessAlert(true);
    },
    onError: (error: any) => {
      setAlertMessage(error.message || "Ürün oluşturulurken hata oluştu");
      setShowErrorAlert(true);
    },
  });

  // Delete product mutation
  const deleteProductMutation = useMutation({
    mutationFn: productsAPI.deleteProduct,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["products"] });
      setAlertMessage("Ürün başarıyla silindi!");
      setShowSuccessAlert(true);
    },
    onError: (error: any) => {
      setAlertMessage(error.message || "Ürün silinirken hata oluştu");
      setShowErrorAlert(true);
    },
  });

  const handleFilterChange = (field: keyof ProductFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
    setPage(0); // Reset to first page when filters change
  };

  const handleSelectProduct = (productId: string) => {
    setSelectedProducts((prev) =>
      prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (!productsData?.data) return;

    if (selectedProducts.length === productsData.data.length) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(productsData.data.map((p) => p._id));
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddProduct = () => {
    setShowAddDialog(true);
  };

  const handleBulkUpload = () => {
    setShowBulkUploadDialog(true);
  };

  const handleCloseAddDialog = () => {
    setShowAddDialog(false);
  };

  const handleCloseBulkUploadDialog = () => {
    setShowBulkUploadDialog(false);
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress(0);

    // Simulate file upload progress
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          setUploading(false);
          setShowBulkUploadDialog(false);
          setAlertMessage("Ürünler başarıyla yüklendi!");
          setShowSuccessAlert(true);
          return 100;
        }
        return prev + 10;
      });
    }, 200);

    // Simulate file processing
    setTimeout(() => {
      clearInterval(interval);
      setUploading(false);
      setShowBulkUploadDialog(false);
      setAlertMessage("Ürünler başarıyla yüklendi!");
      setShowSuccessAlert(true);
    }, 2000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "success";
      case "inactive":
        return "default";
      case "discontinued":
        return "error";
      case "out_of_stock":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "active":
        return "Aktif";
      case "inactive":
        return "Pasif";
      case "discontinued":
        return "Üretimden Kaldırıldı";
      case "out_of_stock":
        return "Stokta Yok";
      default:
        return status;
    }
  };

  const getStockStatusColor = (stockStatus: string) => {
    switch (stockStatus) {
      case "out_of_stock":
        return "error";
      case "low_stock":
        return "warning";
      case "in_stock":
        return "success";
      default:
        return "default";
    }
  };

  const getStockStatusLabel = (stockStatus: string) => {
    switch (stockStatus) {
      case "out_of_stock":
        return "Stokta Yok";
      case "low_stock":
        return "Düşük Stok";
      case "in_stock":
        return "Stokta";
      default:
        return stockStatus;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency: "TRY",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("tr-TR");
  };

  const handleCloseSuccessAlert = () => {
    setShowSuccessAlert(false);
  };

  const handleCloseErrorAlert = () => {
    setShowErrorAlert(false);
  };

  const handleDeleteProduct = (productId: string) => {
    if (window.confirm("Bu ürünü silmek istediğinizden emin misiniz?")) {
      deleteProductMutation.mutate(productId);
    }
  };

  const handleRefresh = () => {
    refetch();
  };

  return (
    <DashboardLayout title="Ürün Yönetimi">
      <Container maxWidth="xl">
        {/* Success/Error Alerts */}
        <Snackbar
          open={showSuccessAlert}
          autoHideDuration={6000}
          onClose={handleCloseSuccessAlert}
        >
          <Alert onClose={handleCloseSuccessAlert} severity="success">
            {alertMessage}
          </Alert>
        </Snackbar>

        <Snackbar
          open={showErrorAlert}
          autoHideDuration={6000}
          onClose={handleCloseErrorAlert}
        >
          <Alert onClose={handleCloseErrorAlert} severity="error">
            {alertMessage}
          </Alert>
        </Snackbar>

        {/* Header with Actions */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h4" component="h1">
            Ürün Yönetimi
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={isLoading}
            >
              Yenile
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={handleBulkUpload}
            >
              Toplu Yükle
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddProduct}
            >
              + Ürün Ekle
            </Button>
          </Box>
        </Box>

        {/* Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Ürün Ara"
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Kategori</InputLabel>
                  <Select
                    value={filters.category}
                    onChange={(e) =>
                      handleFilterChange("category", e.target.value)
                    }
                    label="Kategori"
                  >
                    <MenuItem value="">Tümü</MenuItem>
                    <MenuItem value="Elektronik">Elektronik</MenuItem>
                    <MenuItem value="Bilgisayar">Bilgisayar</MenuItem>
                    <MenuItem value="Giyim">Giyim</MenuItem>
                    <MenuItem value="Ev & Yaşam">Ev & Yaşam</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Marka</InputLabel>
                  <Select
                    value={filters.brand}
                    onChange={(e) =>
                      handleFilterChange("brand", e.target.value)
                    }
                    label="Marka"
                  >
                    <MenuItem value="">Tümü</MenuItem>
                    <MenuItem value="Apple">Apple</MenuItem>
                    <MenuItem value="Samsung">Samsung</MenuItem>
                    <MenuItem value="Dell">Dell</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Durum</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) =>
                      handleFilterChange("status", e.target.value)
                    }
                    label="Durum"
                  >
                    <MenuItem value="">Tümü</MenuItem>
                    <MenuItem value="active">Aktif</MenuItem>
                    <MenuItem value="inactive">Pasif</MenuItem>
                    <MenuItem value="discontinued">
                      Üretimden Kaldırıldı
                    </MenuItem>
                    <MenuItem value="out_of_stock">Stokta Yok</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Products Table */}
        <Card>
          <CardContent>
            {isLoading ? (
              <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                Ürünler yüklenirken hata oluştu: {error.message}
              </Alert>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={
                              selectedProducts.length ===
                                (productsData?.data?.length || 0) &&
                              (productsData?.data?.length || 0) > 0
                            }
                            indeterminate={
                              selectedProducts.length > 0 &&
                              selectedProducts.length <
                                (productsData?.data?.length || 0)
                            }
                            onChange={handleSelectAll}
                          />
                        </TableCell>
                        <TableCell>Ürün</TableCell>
                        <TableCell>Fiyat</TableCell>
                        <TableCell>Stok</TableCell>
                        <TableCell>Kategori</TableCell>
                        <TableCell>Marka</TableCell>
                        <TableCell>Durum</TableCell>
                        <TableCell>Oluşturulma Tarihi</TableCell>
                        <TableCell>İşlemler</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {productsData?.data?.map((product) => (
                        <TableRow key={product._id} hover>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedProducts.includes(product._id)}
                              onChange={() => handleSelectProduct(product._id)}
                            />
                          </TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={2}>
                              <Avatar
                                src={
                                  product.images?.[0]?.url ||
                                  "https://via.placeholder.com/60x60?text=No+Image"
                                }
                                alt={product.name}
                              />
                              <Box>
                                <Typography variant="body2" fontWeight="bold">
                                  {product.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {product.product_code}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {formatCurrency(product.selling_price)}
                            </Typography>
                            {product.discounted_price && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ textDecoration: "line-through" }}
                              >
                                {formatCurrency(product.cost_price)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box>
                              <Typography variant="body2">
                                {product.current_stock} /{" "}
                                {product.available_stock}
                              </Typography>
                              <Chip
                                label={getStockStatusLabel(
                                  product.stock_status
                                )}
                                color={
                                  getStockStatusColor(
                                    product.stock_status
                                  ) as any
                                }
                                size="small"
                              />
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={product.category} size="small" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {product.brand}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusLabel(product.status)}
                              color={getStatusColor(product.status) as any}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(product.created_at)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box display="flex" gap={1}>
                              <Tooltip title="Görüntüle">
                                <IconButton size="small">
                                  <ViewIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Düzenle">
                                <IconButton size="small">
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Sil">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    handleDeleteProduct(product._id)
                                  }
                                  disabled={deleteProductMutation.isPending}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {productsData?.pagination && (
                  <TablePagination
                    component="div"
                    count={productsData.pagination.totalDocs}
                    page={page}
                    onPageChange={handlePageChange}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    labelRowsPerPage="Sayfa başına satır:"
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}-${to} / ${count}`
                    }
                  />
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Add Product Dialog */}
        <Dialog
          open={showAddDialog}
          onClose={handleCloseAddDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Yeni Ürün Ekle</DialogTitle>
          <DialogContent>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Ürün Adı" required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Ürün Kodu" required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Satış Fiyatı"
                  type="number"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Maliyet Fiyatı"
                  type="number"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Açıklama" multiline rows={3} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Kategori</InputLabel>
                  <Select label="Kategori">
                    <MenuItem value="Elektronik">Elektronik</MenuItem>
                    <MenuItem value="Bilgisayar">Bilgisayar</MenuItem>
                    <MenuItem value="Giyim">Giyim</MenuItem>
                    <MenuItem value="Ev & Yaşam">Ev & Yaşam</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Marka</InputLabel>
                  <Select label="Marka">
                    <MenuItem value="Apple">Apple</MenuItem>
                    <MenuItem value="Samsung">Samsung</MenuItem>
                    <MenuItem value="Dell">Dell</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Minimum Stok"
                  type="number"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Mevcut Stok"
                  type="number"
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Etiketler (virgülle ayırın)"
                  placeholder="telefon, akıllı telefon, apple"
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<ImageIcon />}
                  fullWidth
                >
                  Ürün Görseli Yükle
                  <input type="file" hidden accept="image/*" />
                </Button>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseAddDialog}>İptal</Button>
            <Button
              variant="contained"
              onClick={handleCloseAddDialog}
              disabled={createProductMutation.isPending}
            >
              {createProductMutation.isPending ? "Ekleniyor..." : "Ürün Ekle"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Bulk Upload Dialog */}
        <Dialog
          open={showBulkUploadDialog}
          onClose={handleCloseBulkUploadDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Toplu Ürün Yükleme</DialogTitle>
          <DialogContent>
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Excel (.xlsx) veya CSV dosyası yükleyerek toplu ürün
                ekleyebilirsiniz.
              </Typography>
            </Box>

            {uploading && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Dosya yükleniyor... %{uploadProgress}
                </Typography>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}

            <Box sx={{ mb: 2 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<FileUploadIcon />}
                fullWidth
                disabled={uploading}
              >
                Dosya Seç
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.csv"
                  onChange={handleFileUpload}
                />
              </Button>
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom>
                Excel Şablonu
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Ürün Adı"
                    secondary="Ürünün tam adını girin"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Ürün Kodu"
                    secondary="Benzersiz ürün kodu"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Açıklama"
                    secondary="Ürün açıklaması"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Satış Fiyatı"
                    secondary="Sayısal değer (örn: 29999)"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Kategori"
                    secondary="Elektronik, Bilgisayar, vb."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Marka"
                    secondary="Apple, Samsung, vb."
                  />
                </ListItem>
              </List>
            </Box>

            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" startIcon={<DownloadIcon />} fullWidth>
                Örnek Şablonu İndir
              </Button>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseBulkUploadDialog}>İptal</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </DashboardLayout>
  );
};

export default ProductManagement;
